import { useEffect, useRef, useState } from "react";
import { findRegimen, type Regimen } from "@/data/meal-plan";
import type { DayOfWeek, DayPlan } from "@/types/meal-plan-state";
import { useWeekPlan } from "@/context/week-plan-context";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "lucide-react";
import { MealCard } from "./meal-card";
import { RegimenSelector } from "./regimen-selector";
import { DuplicateDayDialog } from "./duplicate-day-dialog";
import { DuplicateWeekDialog } from "./duplicate-week-dialog";
import { DuplicatePreviousWeekDialog } from "./duplicate-previous-week-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { hasEatenMarks, withEatenLossWarning } from "@/lib/owner-eaten";

interface DayPanelProps {
  day: DayOfWeek;
}

export function DayPanel({ day }: DayPanelProps) {
  const { state, dispatch, owner } = useWeekPlan();
  const dayPlan = state.weekPlan[day];
  const [showDuplicateDay, setShowDuplicateDay] = useState(false);
  const [showDuplicateWeek, setShowDuplicateWeek] = useState(false);
  const [showDuplicatePrevWeek, setShowDuplicatePrevWeek] = useState(false);
  const [duplicateMenuOpen, setDuplicateMenuOpen] = useState(false);
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const duplicateMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!duplicateMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (
        duplicateMenuRef.current &&
        !duplicateMenuRef.current.contains(e.target as Node)
      ) {
        setDuplicateMenuOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setDuplicateMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [duplicateMenuOpen]);

  const regimen: Regimen | undefined = findRegimen(dayPlan.regimenId);

  return (
    <div className="space-y-4">
      <RegimenSelector
        value={dayPlan.regimenId}
        onChange={(regimenId) =>
          dispatch({ type: "SET_REGIMEN", day, regimenId })
        }
        regimens={state.regimens}
      />

      {regimen && dayPlan.meals && (
        <>
          <div className="flex gap-2 flex-wrap">
            <div className="relative" ref={duplicateMenuRef}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDuplicateMenuOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={duplicateMenuOpen}
              >
                Duplicar
                <ChevronDownIcon />
              </Button>
              {duplicateMenuOpen && (
                <div
                  role="menu"
                  className="absolute z-20 mt-1 left-0 min-w-[18rem] rounded-md border bg-popover text-popover-foreground shadow-md p-1"
                >
                  <button
                    role="menuitem"
                    className="w-full text-left text-sm rounded-sm px-3 py-2 hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setDuplicateMenuOpen(false);
                      setShowDuplicateDay(true);
                    }}
                  >
                    Duplicar día
                  </button>
                  <button
                    role="menuitem"
                    className="w-full text-left text-sm rounded-sm px-3 py-2 hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setDuplicateMenuOpen(false);
                      setShowDuplicateWeek(true);
                    }}
                  >
                    Duplicar semana
                  </button>
                  <button
                    role="menuitem"
                    className="w-full text-left text-sm rounded-sm px-3 py-2 hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setDuplicateMenuOpen(false);
                      setShowDuplicatePrevWeek(true);
                    }}
                  >
                    Duplicar semana anterior menos almuerzo y cena
                  </button>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowConfirmClear(true)}
            >
              Limpiar
            </Button>
          </div>

          {showDuplicateDay && (
            <DuplicateDayDialog
              targetDay={day}
              targetRegimenId={dayPlan.regimenId!}
              currentWeekId={state.currentWeekId}
              currentWeekPlan={state.weekPlan}
              weekList={state.weekList}
              onDuplicate={(sourcePlan: DayPlan, mealIndices: number[]) => {
                dispatch({ type: "DUPLICATE_MEALS", day, sourcePlan, mealIndices });
                setShowDuplicateDay(false);
              }}
              onClose={() => setShowDuplicateDay(false)}
            />
          )}

          {showDuplicateWeek && (
            <DuplicateWeekDialog
              currentWeekId={state.currentWeekId}
              currentWeekPlan={state.weekPlan}
              weekList={state.weekList}
              onDuplicate={(selections) => {
                dispatch({ type: "DUPLICATE_WEEK", selections });
                setShowDuplicateWeek(false);
              }}
              onClose={() => setShowDuplicateWeek(false)}
            />
          )}

          {showDuplicatePrevWeek && (
            <DuplicatePreviousWeekDialog
              currentWeekId={state.currentWeekId}
              currentWeekPlan={state.weekPlan}
              onDuplicate={(selections) => {
                dispatch({ type: "DUPLICATE_WEEK", selections });
                setShowDuplicatePrevWeek(false);
              }}
              onClose={() => setShowDuplicatePrevWeek(false)}
            />
          )}

          <div className="space-y-4">
            {regimen.meals.map((meal, mealIndex) => (
              <MealCard
                key={meal.name}
                meal={meal}
                selections={dayPlan.meals![mealIndex] ?? []}
                onSelectionChange={(choiceGroupIndex, selections) =>
                  dispatch({
                    type: "SET_SELECTION",
                    day,
                    mealIndex,
                    choiceGroupIndex,
                    selections,
                  })
                }
                note={dayPlan.mealNotes?.[mealIndex]}
                onNoteChange={(note) =>
                  dispatch({ type: "SET_MEAL_NOTE", day, mealIndex, note })
                }
                menuId={dayPlan.mealMenus?.[mealIndex]}
                onMenuChange={(menuId) =>
                  dispatch({ type: "SET_MEAL_MENU", day, mealIndex, menuId })
                }
              />
            ))}
          </div>
        </>
      )}

      {!regimen && (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          Selecciona un tipo de día para comenzar
        </div>
      )}

      <ConfirmDialog
        open={showConfirmClear}
        onOpenChange={setShowConfirmClear}
        title="¿Limpiar las selecciones del día?"
        description={withEatenLossWarning("Esta acción eliminará las selecciones del día.", owner, hasEatenMarks(dayPlan.meals))}
        confirmText="Limpiar"
        destructive
        onConfirm={() => dispatch({ type: "CLEAR_DAY", day })}
      />
    </div>
  );
}
